import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SmoltWeightData } from '@/domain/smoltReport';
import { PDF_KEEP_TOGETHER_ATTR } from '@/utils/canvasToPdf';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import SectionHeader from '../../shared/SectionHeader';

import SmoltWeightDistributionHistogram from './SmoltWeightDistributionHistogram';
import SmoltWeightTimeSeriesCharts from './SmoltWeightTimeSeriesCharts';
import WeightSummaryPanel from './WeightSummaryPanel';

import styles from './WeightAndGrowthSection.module.scss';

interface WeightAndGrowthSectionProps {
    deliveryDate: string;
    lastFeedingDate?: string | null;
    weightData: SmoltWeightData;
    isLoading?: boolean;
    isPdfExport?: boolean;
}

export default function WeightAndGrowthSection(props: WeightAndGrowthSectionProps) {
    const {
        deliveryDate,
        lastFeedingDate,
        weightData,
        isLoading = false,
        isPdfExport = false,
    } = props;

    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    const numberOfMeasurementsLabel = useMemo(() => {
        return `${t('smoltReport.detailView.numberOfMeasurements')} ${formatNumberByLocale(weightData.numMeasurements, locale, 0)}`;
    }, [weightData.numMeasurements, locale, t]);

    // The histogram names the day it is from; a report without that day has no histogram.
    const { distribution, distributionDay } = weightData;
    const showDistribution = distributionDay != null && distribution.length > 0;

    return (
        <div className={styles.sectionContainer}>
            {/* In a PDF, a page break starts a new page above the summary or the histogram
                rather than cutting through it; the heading stays with the summary. */}
            <div {...{ [PDF_KEEP_TOGETHER_ATTR]: 'children' }}>
                <div>
                    <SectionHeader
                        title={t('smoltReport.detailView.weightAndGrowthTitle')}
                        subtitle={t('smoltReport.detailView.weightAndGrowthSubtitle')}
                        rightContent={numberOfMeasurementsLabel}
                    />
                    <WeightSummaryPanel
                        weightData={weightData}
                        deliveryDate={deliveryDate}
                        lastFeedingDate={lastFeedingDate}
                        isLoading={isLoading}
                        isPdfExport={isPdfExport}
                    />
                </div>
                {showDistribution && (
                    <SmoltWeightDistributionHistogram
                        day={distributionDay}
                        distribution={distribution}
                        smallestFish={weightData.smallestFish}
                        largestFish={weightData.largestFish}
                        isLoading={isLoading}
                    />
                )}
            </div>

            <SmoltWeightTimeSeriesCharts
                isPdfExport={isPdfExport}
                isLoading={isLoading}
                timeSeries={weightData.timeSeries ?? []}
                lastFeedingDate={lastFeedingDate}
            />
        </div>
    );
}
